import type { SanitizationResult } from '../types/security';

/**
 * Dangerous patterns that could be used for XSS or injection attacks
 */
const DANGEROUS_PATTERNS = [
  /<script[^>]*>.*?<\/script>/gi,
  /javascript:/gi,
  /on\w+\s*=/gi, // Event handlers like onclick=
  /<iframe/gi,
  /<object/gi,
  /<embed/gi,
  /eval\(/gi,
  /expression\(/gi,
  /vbscript:/gi,
  /data:text\/html/gi
];

/**
 * Dangerous selector patterns
 */
const DANGEROUS_SELECTORS = [
  /javascript:/i,
  /<script/i,
  /on\w+=/i,
  /eval\(/i
];

/**
 * Sanitize user input by removing dangerous patterns
 */
export function sanitizeInput(input: string, maxLength: number = 10000): SanitizationResult {
  if (typeof input !== 'string') {
    return {
      sanitized: String(input),
      wasModified: true,
      removedPatterns: ['non-string-input']
    };
  }

  let sanitized = input;
  const removedPatterns: string[] = [];

  // Truncate if too long
  if (sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength);
    removedPatterns.push('length-exceeded');
  }

  // Remove dangerous patterns
  for (const pattern of DANGEROUS_PATTERNS) {
    if (pattern.test(sanitized)) {
      sanitized = sanitized.replace(pattern, '');
      removedPatterns.push(pattern.toString());
    }
  }

  // HTML entity encode
  sanitized = encodeHtmlEntities(sanitized);

  return {
    sanitized,
    wasModified: sanitized !== input,
    removedPatterns
  };
}

/**
 * Validate and sanitize CSS selector
 */
export function sanitizeSelector(selector: string, maxLength: number = 1000): SanitizationResult {
  if (typeof selector !== 'string') {
    return {
      sanitized: '',
      wasModified: true,
      removedPatterns: ['non-string-selector']
    };
  }

  let sanitized = selector.trim();
  const removedPatterns: string[] = [];

  // Check length
  if (sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength);
    removedPatterns.push('length-exceeded');
  }

  // Check for dangerous patterns
  for (const pattern of DANGEROUS_SELECTORS) {
    if (pattern.test(sanitized)) {
      removedPatterns.push(pattern.toString());
      sanitized = '';
      break;
    }
  }

  // Validate it's a reasonable selector
  if (sanitized && !isValidSelectorSyntax(sanitized)) {
    removedPatterns.push('invalid-syntax');
    sanitized = '';
  }

  return {
    sanitized,
    wasModified: sanitized !== selector.trim(),
    removedPatterns
  };
}

/**
 * Sanitize URL
 */
export function sanitizeUrl(url: string, maxLength: number = 2000): SanitizationResult {
  if (typeof url !== 'string') {
    return {
      sanitized: '',
      wasModified: true,
      removedPatterns: ['non-string-url']
    };
  }

  let sanitized = url.trim();
  const removedPatterns: string[] = [];

  // Check length
  if (sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength);
    removedPatterns.push('length-exceeded');
  }

  // Check for dangerous protocols
  const dangerousProtocols = /^(javascript|data|vbscript|file):/i;
  if (dangerousProtocols.test(sanitized)) {
    removedPatterns.push('dangerous-protocol');
    sanitized = '';
  }

  // Encode special characters
  try {
    // Try to parse and reconstruct URL
    const urlObj = new URL(sanitized, window.location.origin);
    sanitized = urlObj.href;
  } catch {
    // If invalid URL, sanitize as string
    sanitized = encodeHtmlEntities(sanitized);
    removedPatterns.push('invalid-url-format');
  }

  return {
    sanitized,
    wasModified: sanitized !== url.trim() || removedPatterns.length > 0,
    removedPatterns
  };
}

/**
 * HTML entity encode
 */
function encodeHtmlEntities(str: string): string {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

/**
 * Basic selector syntax validation
 */
function isValidSelectorSyntax(selector: string): boolean {
  try {
    // Try to query with the selector to validate syntax
    document.querySelector(selector);
    return true;
  } catch {
    return false;
  }
}

/**
 * Sanitize object (recursively sanitize all string properties)
 */
export function sanitizeObject(obj: any, maxDepth: number = 5): any {
  if (maxDepth <= 0) {
    return obj;
  }

  if (typeof obj === 'string') {
    return sanitizeInput(obj).sanitized;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeObject(item, maxDepth - 1));
  }

  if (obj && typeof obj === 'object') {
    const sanitized: any = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        sanitized[key] = sanitizeObject(obj[key], maxDepth - 1);
      }
    }
    return sanitized;
  }

  return obj;
}
